import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Star, Check, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { StatusBadge } from "@/pages/admin/adminUi";
import SelectedBadge from "@/components/SelectedBadge";

const CATS = [
  { k: "property_excellence", t: "Property Excellence", max: 25 },
  { k: "location_privacy", t: "Location & Privacy", max: 15 },
  { k: "hospitality", t: "Hospitality", max: 20 },
  { k: "guest_experience", t: "Guest Experience", max: 15 },
  { k: "services_experiences", t: "Services & Experiences", max: 10 },
  { k: "digital_readiness", t: "Digital & VillaFlow Readiness", max: 15 },
];
const NN = [
  { k: "reputation_issues", t: "Serious recurring reputation issues" },
  { k: "cleanliness_hygiene", t: "Unacceptable cleanliness or hygiene" },
  { k: "maintenance_safety", t: "Serious maintenance or safety concerns" },
  { k: "unreliable_management", t: "Unreliable management" },
  { k: "misleading_information", t: "Misleading property information" },
  { k: "damaging_partners", t: "Partners that could damage guest experience" },
  { k: "positioning_incompatibility", t: "Incompatibility with VillaFlow positioning" },
];

function Row({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between gap-6 py-2.5 border-b border-ivory/8">
      <span className="text-[11px] tracking-wide uppercase text-ivory/40">{label}</span>
      <span className="text-ivory/85 text-sm text-right">{value}</span>
    </div>
  );
}

const editInput = "w-full bg-navy-900 border border-ivory/15 focus:border-gold outline-none px-3 py-2 text-ivory text-sm transition-colors";

function EditRow({ label, value, onChange, type = "text", options, testId }) {
  return (
    <label className="block py-2">
      <span className="block text-[10px] tracking-wide uppercase text-ivory/40 mb-1">{label}</span>
      {options ? (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={editInput} data-testid={testId}>
          <option value="" className="bg-navy-900">—</option>
          {options.map((o) => <option key={o} value={o} className="bg-navy-900">{o}</option>)}
        </select>
      ) : (
        <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={editInput} data-testid={testId} />
      )}
    </label>
  );
}

const EDIT_PROPERTY_TYPES = ["Luxury Villa", "Private Estate", "Boutique Residence", "Other"];
const EDIT_ROLES = ["Owner", "Property Manager", "Hospitality Company", "Other"];
const EDIT_MARKETS = ["USA", "Canada", "UK", "Europe", "Middle East", "Other"];
const EDIT_SERVICES = ["Private Transfers", "Boat Tours", "Private Chef", "Wine Experiences", "Wellness", "Grocery Delivery", "Car Rental", "Tours & Excursions", "Babysitting", "Romantic Experiences", "Other"];

function ChipSelect({ options, value, onToggle, testIdPrefix }) {
  const val = Array.isArray(value) ? value : [];
  return (
    <div className="flex flex-wrap gap-2 mt-1">
      {options.map((o) => {
        const active = val.includes(o);
        return (
          <button type="button" key={o} onClick={() => onToggle(o)} className={`px-3 py-1.5 text-[10px] tracking-wide border transition-colors ${active ? "border-gold bg-gold/10 text-gold" : "border-ivory/20 text-ivory/55 hover:border-ivory/50"}`} data-testid={testIdPrefix ? `${testIdPrefix}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : undefined}>{o}</button>
        );
      })}
    </div>
  );
}


export default function ApplicationDetail() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [scores, setScores] = useState({});
  const [nn, setNn] = useState({});
  const [note, setNote] = useState("");
  const [pubDesc, setPubDesc] = useState("");
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [editProp, setEditProp] = useState(false);
  const [editMgmt, setEditMgmt] = useState(false);
  const [editGuest, setEditGuest] = useState(false);
  const [editExp, setEditExp] = useState(false);
  const [propForm, setPropForm] = useState({});
  const [mgmtForm, setMgmtForm] = useState({});
  const [guestForm, setGuestForm] = useState({});
  const [expForm, setExpForm] = useState({});
  const fileRef = useRef(null);

  const load = () =>
    api.get(`/admin/applications/${id}`).then((r) => {
      setApp(r.data);
      setScores(r.data.assessment?.scores || {});
      setNn(r.data.assessment?.non_negotiables || {});
      setPubDesc(r.data.public_description || "");
      setImages(r.data.images || []);
      setPropForm(r.data.property || {});
      setMgmtForm(r.data.management || {});
      setGuestForm(r.data.guest_profile || {});
      setExpForm(r.data.experience || {});
    });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!app) return <p className="text-ivory/40">Loading…</p>;

  const total = CATS.reduce((sum, c) => sum + Math.min(Number(scores[c.k] || 0), c.max), 0);
  const failedNN = NN.filter((n) => nn[n.k]).length;
  const recommended = failedNN > 0 ? "NOT_SELECTED" : total >= 85 ? "SELECTED" : "UNDER_REVIEW";

  const setStatus = async (status, msg) => {
    try {
      await api.patch(`/admin/applications/${id}/status`, { status, note: msg || "" });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveAssessment = async () => {
    try {
      const { data } = await api.put(`/admin/applications/${id}/assessment`, { scores, non_negotiables: nn });
      toast.success(`Assessment saved — ${data.assessment.total}/100 · Recommended ${data.assessment.recommended_result.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleFounding = async () => {
    try {
      await api.patch(`/admin/applications/${id}/founding`, { founding_member: !app.founding_member });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const togglePublish = async () => {
    try {
      await api.patch(`/admin/applications/${id}/publish`, { published: !app.published, public_description: pubDesc });
      toast.success(!app.published ? "Published to public Collection" : "Removed from public Collection");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/admin/applications/${id}/notes`, { note });
    setNote("");
    load();
  };

  const saveImages = async (newImages, hero) => {
    const { data } = await api.patch(`/admin/applications/${id}/images`, { images: newImages, hero_image: hero || null });
    setImages(data.images);
    setApp((a) => ({ ...a, images: data.images, hero_image: data.hero_image }));
  };

  const uploadFiles = async (files) => {
    const chosen = Array.from(files).slice(0, 40 - images.length);
    let next = [...images];
    for (const file of chosen) {
      setUploading((u) => u + 1);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        next = [...next, data.path];
        await saveImages(next, app.hero_image);
      } catch (e) {
        toast.error("Upload failed");
      } finally {
        setUploading((u) => u - 1);
      }
    }
    toast.success("Photos added");
  };

  const removeImage = async (path) => {
    const next = images.filter((p) => p !== path);
    await saveImages(next, app.hero_image === path ? next[0] : app.hero_image);
  };

  const saveProperty = async () => {
    try {
      await api.patch(`/admin/applications/${id}/details`, { property: propForm });
      toast.success("Property details updated");
      setEditProp(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveManagement = async () => {
    try {
      await api.patch(`/admin/applications/${id}/details`, { management: mgmtForm });
      toast.success("Management details updated");
      setEditMgmt(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveGuest = async () => {
    try {
      await api.patch(`/admin/applications/${id}/details`, { guest_profile: guestForm });
      toast.success("Guest profile updated");
      setEditGuest(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveExperience = async () => {
    try {
      await api.patch(`/admin/applications/${id}/details`, { experience: expForm });
      toast.success("Property experience updated");
      setEditExp(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleInList = (form, setForm, key, val) => {
    const arr = Array.isArray(form[key]) ? form[key] : [];
    setForm({ ...form, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] });
  };

  const p = app.property || {}, mgmt = app.management || {}, gp = app.guest_profile || {}, exp = app.experience || {};

  return (
    <div data-testid="application-detail" className="max-w-6xl">
      <Link to="/admin/applications" className="inline-flex items-center gap-2 text-ivory/50 hover:text-gold text-xs tracking-[0.18em] uppercase mb-8"><ArrowLeft size={14} /> All Applications</Link>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-10">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-serif font-light text-4xl text-ivory">{p.name}</h1>
            {app.founding_member && <span className="text-[9px] tracking-widest uppercase text-gold border border-gold/40 px-2 py-1">Founding Member</span>}
          </div>
          <p className="text-ivory/45 mt-2">{[p.city, p.location].filter(Boolean).join(", ")}</p>
        </div>
        <StatusBadge status={app.status} className="text-xs" />
      </div>

      {/* Decision bar */}
      <div className="flex flex-wrap gap-2 mb-12" data-testid="decision-bar">
        <button onClick={() => setStatus("PRE_SCREENING", "Assessment started")} className="px-5 py-3 text-[10px] tracking-[0.18em] uppercase border border-ivory/25 text-ivory/70 hover:border-gold hover:text-gold transition-colors" data-testid="btn-start-assessment">Start Assessment</button>
        <button onClick={() => setStatus("COMMERCIAL_CONTACT", "Commercial contact")} className="px-5 py-3 text-[10px] tracking-[0.18em] uppercase border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 transition-colors" data-testid="btn-contact">Contact Property</button>
        <button onClick={() => setStatus("SELECTED", "Selected")} className="px-5 py-3 text-[10px] tracking-[0.18em] uppercase border border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/10 transition-colors" data-testid="btn-select">Select Property</button>
        <button onClick={() => setStatus("WAITLIST", "Waitlisted")} className="px-5 py-3 text-[10px] tracking-[0.18em] uppercase border border-purple-400/40 text-purple-300 hover:bg-purple-400/10 transition-colors" data-testid="btn-waitlist">Waitlist</button>
        <button onClick={() => setStatus("NOT_SELECTED", "Not selected")} className="px-5 py-3 text-[10px] tracking-[0.18em] uppercase border border-red-400/40 text-red-300 hover:bg-red-400/10 transition-colors" data-testid="btn-not-selected">Not Selected</button>
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-12">
          {/* Images manager */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[11px] tracking-[0.25em] uppercase text-ivory/50">Property Images</h2>
              <span className="text-ivory/35 text-xs">{images.length} photo{images.length === 1 ? "" : "s"} · click ★ to set hero</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="detail-images">
              {images.map((img, i) => (
                <div key={img + i} className={`relative aspect-[4/3] overflow-hidden border group ${app.hero_image === img ? "border-gold" : "border-ivory/10"}`}>
                  <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-navy-900/0 group-hover:bg-navy-900/40 transition-colors" />
                  <button type="button" onClick={() => saveImages(images, img)} title="Set as hero" className={`absolute top-2 left-2 p-1.5 ${app.hero_image === img ? "text-gold" : "text-ivory/70 opacity-0 group-hover:opacity-100"} transition-opacity`} data-testid={`set-hero-${i}`}>
                    <Star size={14} className={app.hero_image === img ? "fill-gold" : ""} />
                  </button>
                  <button type="button" onClick={() => removeImage(img)} className="absolute top-2 right-2 bg-navy-900/80 text-ivory p-1.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-img-${i}`}><X size={12} /></button>
                  {app.hero_image === img && <span className="absolute bottom-2 left-2 text-[8px] tracking-widest uppercase text-navy bg-gold px-2 py-0.5">Hero</span>}
                </div>
              ))}
              {uploading > 0 && <div className="aspect-[4/3] border border-ivory/10 flex items-center justify-center"><Loader2 className="animate-spin text-gold/60" size={20} /></div>}
              <button type="button" onClick={() => fileRef.current?.click()} className="aspect-[4/3] border border-dashed border-ivory/25 hover:border-gold text-ivory/40 hover:text-gold flex flex-col items-center justify-center gap-2 transition-colors" data-testid="admin-upload-btn">
                <Upload size={18} /><span className="text-[9px] tracking-widest uppercase">Add Photos</span>
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => uploadFiles(e.target.files)} data-testid="admin-file-input" />
          </div>


          {/* Assessment */}
          <div id="assessment" className="border border-ivory/10 bg-navy-800 p-6 md:p-8" data-testid="assessment-panel">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[11px] tracking-[0.25em] uppercase text-gold">Digital Selection Assessment</h2>
              <span className="text-ivory/40 text-xs">Internal · not public</span>
            </div>
            <div className="space-y-5">
              {CATS.map((c) => (
                <div key={c.k}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-ivory/75">{c.t}</span>
                    <span className="text-ivory/50">{Math.min(Number(scores[c.k] || 0), c.max)} / {c.max}</span>
                  </div>
                  <input type="range" min="0" max={c.max} value={scores[c.k] || 0} onChange={(e) => setScores((s) => ({ ...s, [c.k]: Number(e.target.value) }))} data-testid={`score-${c.k}`} className="w-full accent-[#C6A15B]" />
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-center justify-between border-t border-ivory/10 pt-6">
              <span className="text-[11px] tracking-[0.2em] uppercase text-ivory/50">Total Score</span>
              <span className={`font-serif text-4xl ${total >= 85 ? "text-emerald-300" : "text-ivory"}`} data-testid="assessment-total">{total}<span className="text-ivory/30 text-2xl">/100</span></span>
            </div>
            <p className="text-[11px] text-ivory/40 mt-1 text-right">Required threshold · 85</p>

            {/* Non-negotiables */}
            <h3 className="text-[11px] tracking-[0.25em] uppercase text-ivory/50 mt-8 mb-4">Non-Negotiable Checks</h3>
            <div className="space-y-2">
              {NN.map((n) => (
                <label key={n.k} className="flex items-center gap-3 cursor-pointer py-1.5" data-testid={`nn-${n.k}`}>
                  <span className={`w-5 h-5 border flex items-center justify-center shrink-0 transition-colors ${nn[n.k] ? "border-red-400 bg-red-400/20" : "border-ivory/25"}`}>{nn[n.k] && <Check size={12} className="text-red-300" />}</span>
                  <input type="checkbox" checked={!!nn[n.k]} onChange={(e) => setNn((s) => ({ ...s, [n.k]: e.target.checked }))} className="sr-only" />
                  <span className={`text-sm ${nn[n.k] ? "text-red-300" : "text-ivory/60"}`}>{n.t}</span>
                </label>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-ivory/10 pt-6">
              <div>
                <span className="text-[11px] tracking-[0.2em] uppercase text-ivory/40">Recommended Result</span>
                <p className="mt-1"><StatusBadge status={recommended} /></p>
                {failedNN > 0 && <p className="text-red-300 text-xs mt-2">Non-negotiable failure → automatic Not Selected.</p>}
              </div>
              <button onClick={saveAssessment} className="bg-ivory hover:bg-gold text-navy px-8 py-3.5 text-[11px] tracking-[0.2em] uppercase transition-colors" data-testid="save-assessment-btn">Save Assessment</button>
            </div>
          </div>

          {/* Notes */}
          <div data-testid="notes-panel">
            <h2 className="text-[11px] tracking-[0.25em] uppercase text-ivory/50 mb-4">Private Internal Notes</h2>
            <p className="text-ivory/35 text-xs mb-3">Never shown to applicants.</p>
            <div className="flex gap-3">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" className="flex-1 bg-transparent border-b border-ivory/20 focus:border-gold outline-none py-2 text-ivory text-sm" data-testid="note-input" />
              <button onClick={addNote} className="border border-ivory/25 hover:border-gold text-ivory/70 hover:text-gold px-5 text-[10px] tracking-widest uppercase transition-colors" data-testid="add-note-btn">Add</button>
            </div>
            <div className="mt-5 space-y-3">
              {(app.internal_notes || []).slice().reverse().map((n, i) => (
                <div key={i} className="bg-navy-800 border border-ivory/10 p-4 text-sm text-ivory/70">{n.note}<span className="block text-[10px] text-ivory/30 mt-2">{(n.at || "").slice(0, 16).replace("T", " ")}</span></div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-8">
          <div className="border border-ivory/10 bg-navy-800 p-6" data-testid="property-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[11px] tracking-[0.25em] uppercase text-gold">Property</h2>
              {!editProp ? (
                <button onClick={() => setEditProp(true)} className="text-[10px] tracking-widest uppercase text-ivory/50 hover:text-gold transition-colors" data-testid="edit-property-btn">Edit</button>
              ) : (
                <div className="flex gap-3">
                  <button onClick={() => { setEditProp(false); setPropForm(app.property || {}); }} className="text-[10px] tracking-widest uppercase text-ivory/40 hover:text-ivory" data-testid="cancel-property-btn">Cancel</button>
                  <button onClick={saveProperty} className="text-[10px] tracking-widest uppercase text-gold hover:text-gold-soft" data-testid="save-property-btn">Save</button>
                </div>
              )}
            </div>
            {!editProp ? (
              <>
                {p.website_url && <a href={p.website_url.startsWith("http") ? p.website_url : `https://${p.website_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-gold hover:text-gold-soft text-sm mb-4 break-all" data-testid="property-website-link"><ExternalLink size={14} /> {p.website_url}</a>}
                <Row label="Name" value={p.name} />
                <Row label="Location" value={p.location} />
                <Row label="City" value={p.city} />
                <Row label="Type" value={p.property_type} />
                <Row label="Bedrooms" value={p.bedrooms} />
                <Row label="Max Guests" value={p.max_guests} />
              </>
            ) : (
              <div data-testid="property-edit-form">
                <EditRow label="Name" testId="property-name-input" value={propForm.name} onChange={(v) => setPropForm({ ...propForm, name: v })} />
                <EditRow label="Website / Listing URL" testId="property-website-input" value={propForm.website_url} onChange={(v) => setPropForm({ ...propForm, website_url: v })} />
                <EditRow label="Location / Region" testId="property-location-input" value={propForm.location} onChange={(v) => setPropForm({ ...propForm, location: v })} />
                <EditRow label="City" testId="property-city-input" value={propForm.city} onChange={(v) => setPropForm({ ...propForm, city: v })} />
                <EditRow label="Property type" testId="property-type-select" value={propForm.property_type} onChange={(v) => setPropForm({ ...propForm, property_type: v })} options={EDIT_PROPERTY_TYPES} />
                <EditRow label="Bedrooms" testId="property-bedrooms-input" type="number" value={propForm.bedrooms} onChange={(v) => setPropForm({ ...propForm, bedrooms: v })} />
                <EditRow label="Max guests" testId="property-max-guests-input" type="number" value={propForm.max_guests} onChange={(v) => setPropForm({ ...propForm, max_guests: v })} />
              </div>
            )}
          </div>

          <div className="border border-ivory/10 bg-navy-800 p-6" data-testid="management-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[11px] tracking-[0.25em] uppercase text-gold">Management</h2>
              {!editMgmt ? (
                <button onClick={() => setEditMgmt(true)} className="text-[10px] tracking-widest uppercase text-ivory/50 hover:text-gold transition-colors" data-testid="edit-management-btn">Edit</button>
              ) : (
                <div className="flex gap-3">
                  <button onClick={() => { setEditMgmt(false); setMgmtForm(app.management || {}); }} className="text-[10px] tracking-widest uppercase text-ivory/40 hover:text-ivory" data-testid="cancel-management-btn">Cancel</button>
                  <button onClick={saveManagement} className="text-[10px] tracking-widest uppercase text-gold hover:text-gold-soft" data-testid="save-management-btn">Save</button>
                </div>
              )}
            </div>
            {!editMgmt ? (
              <>
                <Row label="Contact" value={mgmt.contact_name} />
                <Row label="Role" value={mgmt.role} />
                <Row label="Email" value={mgmt.email} />
                <Row label="Phone" value={mgmt.phone} />
                <Row label="Company" value={mgmt.company} />
                <Row label="Years" value={mgmt.years_operating} />
                <Row label="Properties" value={mgmt.properties_managed} />
              </>
            ) : (
              <div data-testid="management-edit-form">
                <EditRow label="Owner / Property Manager" testId="management-contact-input" value={mgmtForm.contact_name} onChange={(v) => setMgmtForm({ ...mgmtForm, contact_name: v })} />
                <EditRow label="Role" testId="management-role-select" value={mgmtForm.role} onChange={(v) => setMgmtForm({ ...mgmtForm, role: v })} options={EDIT_ROLES} />
                <EditRow label="Email" testId="management-email-input" type="email" value={mgmtForm.email} onChange={(v) => setMgmtForm({ ...mgmtForm, email: v })} />
                <EditRow label="Phone" testId="management-phone-input" value={mgmtForm.phone} onChange={(v) => setMgmtForm({ ...mgmtForm, phone: v })} />
                <EditRow label="Company" testId="management-company-input" value={mgmtForm.company} onChange={(v) => setMgmtForm({ ...mgmtForm, company: v })} />
                <EditRow label="Years operating" testId="management-years-input" type="number" value={mgmtForm.years_operating} onChange={(v) => setMgmtForm({ ...mgmtForm, years_operating: v })} />
                <EditRow label="Properties managed" testId="management-properties-input" type="number" value={mgmtForm.properties_managed} onChange={(v) => setMgmtForm({ ...mgmtForm, properties_managed: v })} />
              </div>
            )}
          </div>

          <div className="border border-ivory/10 bg-navy-800 p-6" data-testid="guest-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[11px] tracking-[0.25em] uppercase text-gold">Guest Profile</h2>
              {!editGuest ? (
                <button onClick={() => setEditGuest(true)} className="text-[10px] tracking-widest uppercase text-ivory/50 hover:text-gold transition-colors" data-testid="edit-guest-btn">Edit</button>
              ) : (
                <div className="flex gap-3">
                  <button onClick={() => { setEditGuest(false); setGuestForm(app.guest_profile || {}); }} className="text-[10px] tracking-widest uppercase text-ivory/40 hover:text-ivory" data-testid="cancel-guest-btn">Cancel</button>
                  <button onClick={saveGuest} className="text-[10px] tracking-widest uppercase text-gold hover:text-gold-soft" data-testid="save-guest-btn">Save</button>
                </div>
              )}
            </div>
            {!editGuest ? (
              <>
                <Row label="Markets" value={(gp.markets || []).join(", ")} />
                <Row label="Profile" value={gp.avg_guest_profile} />
                <Row label="Direct %" value={gp.direct_bookings_pct} />
                <Row label="Channels" value={gp.booking_channels} />
                <Row label="Source" value={app.invitation_source} />
              </>
            ) : (
              <div data-testid="guest-edit-form">
                <div className="py-2">
                  <span className="block text-[10px] tracking-wide uppercase text-ivory/40 mb-1">Primary guest markets</span>
                  <ChipSelect options={EDIT_MARKETS} value={guestForm.markets} onToggle={(v) => toggleInList(guestForm, setGuestForm, "markets", v)} testIdPrefix="guest-market-chip" />
                </div>
                <EditRow label="Average guest profile" testId="guest-profile-input" value={guestForm.avg_guest_profile} onChange={(v) => setGuestForm({ ...guestForm, avg_guest_profile: v })} />
                <EditRow label="Direct bookings %" testId="guest-direct-bookings-input" type="number" value={guestForm.direct_bookings_pct} onChange={(v) => setGuestForm({ ...guestForm, direct_bookings_pct: v })} />
                <EditRow label="Main booking channels" testId="guest-booking-channels-input" value={guestForm.booking_channels} onChange={(v) => setGuestForm({ ...guestForm, booking_channels: v })} />
              </div>
            )}
          </div>

          <div className="border border-ivory/10 bg-navy-800 p-6" data-testid="experience-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[11px] tracking-[0.25em] uppercase text-gold">What Makes It Exceptional</h2>
              {!editExp ? (
                <button onClick={() => setEditExp(true)} className="text-[10px] tracking-widest uppercase text-ivory/50 hover:text-gold transition-colors" data-testid="edit-experience-btn">Edit</button>
              ) : (
                <div className="flex gap-3">
                  <button onClick={() => { setEditExp(false); setExpForm(app.experience || {}); }} className="text-[10px] tracking-widest uppercase text-ivory/40 hover:text-ivory" data-testid="cancel-experience-btn">Cancel</button>
                  <button onClick={saveExperience} className="text-[10px] tracking-widest uppercase text-gold hover:text-gold-soft" data-testid="save-experience-btn">Save</button>
                </div>
              )}
            </div>
            {!editExp ? (
              <>
                {exp.what_makes_exceptional ? <p className="text-ivory/70 text-sm leading-relaxed italic">"{exp.what_makes_exceptional}"</p> : <p className="text-ivory/35 text-sm italic">No description yet.</p>}
                {exp.services?.length > 0 && <div className="mt-4 flex flex-wrap gap-2">{exp.services.map((s) => <span key={s} className="text-[10px] tracking-wide text-ivory/60 border border-ivory/15 px-2 py-1">{s}</span>)}</div>}
              </>
            ) : (
              <div data-testid="experience-edit-form">
                <span className="block text-[10px] tracking-wide uppercase text-ivory/40 mb-1">What makes the property exceptional</span>
                <textarea rows={4} value={expForm.what_makes_exceptional || ""} onChange={(e) => setExpForm({ ...expForm, what_makes_exceptional: e.target.value })} className={`${editInput} resize-none`} data-testid="exp-textarea" />
                <div className="py-2 mt-2">
                  <span className="block text-[10px] tracking-wide uppercase text-ivory/40 mb-1">Services offered</span>
                  <ChipSelect options={EDIT_SERVICES} value={expForm.services} onToggle={(v) => toggleInList(expForm, setExpForm, "services", v)} testIdPrefix="experience-service-chip" />
                </div>
              </div>
            )}
          </div>

          {/* Founding + Publish (only meaningful when selected) */}
          <div className="border border-gold/25 bg-navy-800 p-6 space-y-5">
            <button onClick={toggleFounding} className="w-full flex items-center justify-between text-sm" data-testid="founding-toggle">
              <span className="inline-flex items-center gap-2 text-ivory/80"><Star size={15} className={app.founding_member ? "text-gold fill-gold" : "text-ivory/40"} /> Founding Member</span>
              <span className={`px-3 py-1 text-[10px] tracking-widest uppercase border ${app.founding_member ? "border-gold text-gold" : "border-ivory/25 text-ivory/50"}`}>{app.founding_member ? "Yes" : "No"}</span>
            </button>
            <div className="border-t border-ivory/10 pt-5">
              <label className="block text-[10px] tracking-[0.2em] uppercase text-ivory/45 mb-2">Public description</label>
              <textarea rows={3} value={pubDesc} onChange={(e) => setPubDesc(e.target.value)} className="w-full bg-navy-900 border border-ivory/15 focus:border-gold outline-none p-3 text-ivory/80 text-sm resize-none" data-testid="public-description" />
              <button onClick={togglePublish} className={`mt-3 w-full py-3 text-[10px] tracking-[0.2em] uppercase transition-colors ${app.published ? "border border-red-400/40 text-red-300 hover:bg-red-400/10" : "bg-ivory hover:bg-gold text-navy"}`} data-testid="publish-toggle">
                {app.published ? "Remove from Public Collection" : "Publish to Public Collection"}
              </button>
              <p className="text-ivory/30 text-[10px] mt-2">Requires status “Selected”. Published: {app.published ? "Yes" : "No"}</p>
            </div>
          </div>

          {app.status === "SELECTED" && (
            <div className="border border-ivory/10 bg-navy-800 p-6 flex flex-col items-center">
              <h2 className="text-[11px] tracking-[0.25em] uppercase text-gold mb-4 self-start">Member Badge</h2>
              <SelectedBadge propertyName={p.name} size={220} />
            </div>
          )}

          {/* History */}
          <div className="border border-ivory/10 bg-navy-800 p-6" data-testid="history-panel">
            <h2 className="text-[11px] tracking-[0.25em] uppercase text-gold mb-4">Application History</h2>
            <div className="space-y-3">
              {(app.history || []).slice().reverse().map((h, i) => (
                <div key={i} className="text-xs">
                  <p className="text-ivory/75">{h.action}</p>
                  {h.note && <p className="text-ivory/40">{h.note}</p>}
                  <p className="text-ivory/25 mt-0.5">{(h.at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
